/**
 * VirtualCardGrid — VirtuosoGrid wrapper for responsive card grids.
 *
 * Use when a grid can grow past ~100 items (Inventory power-sellers,
 * Market all-results, long Storefronts). Below that threshold, stick with a
 * plain CSS grid + `.cv-auto` — the React overhead isn't worth it.
 *
 * The outer element keeps the project's existing `inventory-grid` /
 * tailwind grid CSS, so card styling/spacing is unchanged. Children render
 * with their original keys; selection/focus/scroll restoration are handled
 * by Virtuoso internally.
 *
 * Pattern:
 *   {items.length > 100 ? (
 *     <VirtualCardGrid
 *       items={items}
 *       gridClassName="inventory-grid"
 *       renderItem={(item) => <CollectionItemCard item={item} ... />}
 *     />
 *   ) : (
 *     <div className="inventory-grid">{items.map(...)}</div>
 *   )}
 */
import { useEffect } from 'react';
import { VirtuosoGrid, VirtuosoGridHandle, Virtuoso, VirtuosoHandle } from 'react-virtuoso';

/**
 * react-virtuoso manages its own scroll anchoring. If a parent scroll
 * container has `overflow-anchor: auto` (which our `.main-content-scroll`
 * default sets to help non-virtual pages stay put during async hydration),
 * the browser's scroll anchor algorithm fights Virtuoso's item-size
 * recomputation and produces a 1–2px scroll oscillation ("jitter") when the
 * user pauses mid-scroll. Toggle `overflow-anchor: none` on the mounted
 * scroll parent for as long as the virtual list is alive, then restore it.
 */
function useDisableScrollAnchor(el: HTMLElement | null | undefined) {
  useEffect(() => {
    if (!el) return;
    const prev = el.style.overflowAnchor;
    el.style.overflowAnchor = 'none';
    return () => {
      el.style.overflowAnchor = prev;
    };
  }, [el]);
}

interface VirtualCardGridProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Tailwind grid classes — same value you'd pass to <div className=...>. */
  gridClassName?: string;
  /** Key extractor — defaults to `item.id` if present, else array index. */
  getKey?: (item: T, index: number) => string | number;
  /** Pixels of off-screen content to keep rendered. Default 600. */
  overscan?: number;
  /** Optional className for the outer scroll container. */
  className?: string;
  /** Optional ref to the underlying VirtuosoGridHandle for scroll control. */
  handleRef?: React.Ref<VirtuosoGridHandle>;
  /**
   * If provided, VirtuosoGrid uses this element as its scroll parent instead
   * of window scroll. Use when the page already has an inner scroll container
   * (e.g. PageShell scroll="none" wrapping a `.main-content-scroll` div).
   */
  customScrollParent?: HTMLElement | null;
}

export function VirtualCardGrid<T extends { id?: string }>({
  items,
  renderItem,
  gridClassName = 'inventory-grid',
  getKey,
  overscan = 1200,
  className,
  handleRef,
  customScrollParent,
}: VirtualCardGridProps<T>) {
  useDisableScrollAnchor(customScrollParent);
  return (
    <div
      className={className}
      style={{ minHeight: 0, height: customScrollParent ? '100%' : 'auto', overflowAnchor: 'none' }}
    >
      <VirtuosoGrid
        ref={handleRef}
        {...(customScrollParent
          ? { customScrollParent }
          : { useWindowScroll: true })}
        data={items}
        overscan={overscan}
        computeItemKey={(index, item) =>
          getKey?.(item, index) ?? item?.id ?? index
        }
        listClassName={gridClassName}
        itemContent={(index, item) => renderItem(item, index)}
      />
    </div>
  );
}

/**
 * VirtualList — Virtuoso wrapper for 52px row layouts (Inventory list view,
 * Wantlist list, TransactionHistory).
 *
 * Like VirtualCardGrid, gate adoption on item count — under ~100 rows the
 * existing flex/grid + `.cv-auto` is already fast enough.
 */

interface VirtualListProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Approximate row height in px — informs initial overscan window. */
  rowHeight?: number;
  /** Pixels of off-screen content to keep rendered. Default 800. */
  overscan?: number;
  className?: string;
  getKey?: (item: T, index: number) => string | number;
  handleRef?: React.Ref<VirtuosoHandle>;
  /** Optional sticky header rendered above the scrolling list. */
  header?: () => React.ReactNode;
  /**
   * If provided, Virtuoso uses this element as its scroll parent instead of
   * window scroll. Use when the page already has an inner scroll container
   * (e.g. PageShell scroll="none" wrapping a `.main-content-scroll` div).
   */
  customScrollParent?: HTMLElement | null;
}

export function VirtualList<T extends { id?: string }>({
  items,
  renderItem,
  rowHeight = 52,
  overscan = 1500,
  className,
  getKey,
  handleRef,
  header,
  customScrollParent,
}: VirtualListProps<T>) {
  useDisableScrollAnchor(customScrollParent);
  return (
    <div
      className={className}
      style={{ minHeight: 0, height: customScrollParent ? '100%' : 'auto', overflowAnchor: 'none' }}
    >
      <Virtuoso
        ref={handleRef}
        {...(customScrollParent
          ? { customScrollParent }
          : { useWindowScroll: true })}
        data={items}
        overscan={overscan}
        // Pre-mount rows ahead of the viewport so fast scroll never reveals
        // blank space before the row hydrates.
        increaseViewportBy={{ top: 200, bottom: 500 }}
        defaultItemHeight={rowHeight}
        computeItemKey={(index, item) =>
          getKey?.(item, index) ?? item?.id ?? index
        }
        components={header ? { Header: header } : undefined}
        itemContent={(index, item) => renderItem(item, index)}
      />
    </div>
  );
}
